import React, { useState, useEffect, useMemo } from "react";
import moment from "moment-timezone";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import FromDateTimeInput from "./FromDateTimeInput";
import ToDateTimeInput from "./ToDateTimeInput";
import EventTypeSelector from "./EventTypeSelector";
import { errorRed, grayLight, moovsBlue } from "globals/design-system/colors";
import { DriverScheduleSettingScheduleTypeEnum } from "types";

const { Available, Unavailable } = DriverScheduleSettingScheduleTypeEnum;

export type RepeatOption = "EVERYDAY" | "WEEKLY";

export type AddEventFormData = {
  eventType: DriverScheduleSettingScheduleTypeEnum;
  startDateTime: string;
  endDateTime: string;
  repeatOption?: RepeatOption;
};

type AddEventDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddEventFormData) => void;
  clickedCellDate?: Date;
};

function AddEventDialog(params: AddEventDialogProps) {
  const { open, onClose, onSubmit, clickedCellDate } = params;

  // state
  const [formData, setFormData] = useState<AddEventFormData>({
    eventType: Available,
    startDateTime: "",
    endDateTime: "",
    repeatOption: null,
  });
  const [errorMessage, setErrorMessage] = useState("");

  // useMemo
  const validationErrorMessage = useMemo(() => {
    if (!formData.startDateTime) {
      return "Please select a start date and time";
    }

    if (!formData.endDateTime) {
      return "Please select an end date and time";
    }

    if (new Date(formData.startDateTime) >= new Date(formData.endDateTime)) {
      return "End time must be after start time";
    }

    return "";
  }, [formData]);

  // Update error message whenever form data changes
  useEffect(() => {
    setErrorMessage(validationErrorMessage);
  }, [validationErrorMessage]);

  // initialize the start and end date times for the form
  useEffect(() => {
    if (open) {
      let startDate: moment.Moment;

      if (clickedCellDate) {
        const baseDate = moment(clickedCellDate);
        // Check if the time is at midnight (from month view click)
        const isMidnight = baseDate.hour() === 0 && baseDate.minute() === 0;

        if (isMidnight) {
          // Month view click - set to 9 AM
          startDate = baseDate.clone().hour(9).minute(0).second(0);
        } else {
          // Week/Day view click - use the clicked time
          startDate = baseDate;
        }
      } else {
        // No initial date - use current time
        startDate = moment();
      }

      const defaultEnd = startDate.clone().add(1, "hour");

      setFormData({
        eventType: Available,
        startDateTime: startDate.toISOString(),
        endDateTime: defaultEnd.toISOString(),
        repeatOption: null,
      });

      setErrorMessage("");
    }
  }, [open, clickedCellDate]);

  // Event handlers
  const handleEventTypeChange = (
    eventType: DriverScheduleSettingScheduleTypeEnum
  ) => {
    setFormData((prev) => ({
      ...prev,
      eventType,
      repeatOption: eventType === Unavailable ? null : prev.repeatOption,
    }));
  };

  const handleRepeatChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      repeatOption: checked ? "EVERYDAY" : null,
    }));
  };

  const handleRepeatOptionChange = (option: RepeatOption) => {
    setFormData((prev) => ({ ...prev, repeatOption: option }));
  };

  const handleDateChange = (date: Date, type: "start" | "end") => {
    const currentDateTime = moment(
      type === "start" ? formData.startDateTime : formData.endDateTime
    );

    // Create new datetime preserving the original time
    const newDateTime = moment(date)
      .hour(currentDateTime.hour())
      .minute(currentDateTime.minute());

    setFormData((prev) => ({
      ...prev,
      [type === "start" ? "startDateTime" : "endDateTime"]:
        newDateTime.toISOString(),
    }));
  };

  const handleTimeChange = (time: any, type: "start" | "end") => {
    const currentDateTime = moment(
      type === "start" ? formData.startDateTime : formData.endDateTime
    );

    const newDateTime = currentDateTime
      .clone()
      .hour(moment(time).hour())
      .minute(moment(time).minute());

    setFormData((prev) => ({
      ...prev,
      [type === "start" ? "startDateTime" : "endDateTime"]:
        newDateTime.toISOString(),
    }));
  };

  const handleAddEventClick = () => {
    if (!!validationErrorMessage) {
      setErrorMessage(validationErrorMessage);
    } else {
      setErrorMessage("");
      onSubmit(formData);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 480,
          minHeight: 520,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h3" fontWeight={600}>
          Add Event
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: grayLight }} />

      <DialogContent sx={{ pt: 4 }}>
        {/* Event Type Selection */}
        <EventTypeSelector
          value={formData.eventType}
          onChange={handleEventTypeChange}
        />

        {/* FROM Date and Time Selection */}
        <FromDateTimeInput
          value={formData.startDateTime ? moment(formData.startDateTime) : null}
          onDateChange={(date) => handleDateChange(date, "start")}
          onTimeChange={(time) => handleTimeChange(time, "start")}
        />

        {/* TO Date and Time Selection */}
        <ToDateTimeInput
          value={formData.endDateTime ? moment(formData.endDateTime) : null}
          onDateChange={(date) => handleDateChange(date, "end")}
          onTimeChange={(time) => handleTimeChange(time, "end")}
        />

        {/* Repeat Option - Only for Available events */}
        {formData.eventType === Available && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.repeatOption}
                  onChange={(e) => handleRepeatChange(e.target.checked)}
                  sx={{
                    color: moovsBlue,
                    "&.Mui-checked": {
                      color: moovsBlue,
                    },
                  }}
                />
              }
              label="Repeat"
              sx={{ mb: formData.repeatOption ? 2 : 0 }}
            />

            {formData.repeatOption && (
              <TextField
                select
                fullWidth
                value={formData.repeatOption}
                onChange={(e) =>
                  handleRepeatOptionChange(e.target.value as RepeatOption)
                }
                size="small"
              >
                <MenuItem value="EVERYDAY">Everyday</MenuItem>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
              </TextField>
            )}
          </Box>
        )}
      </DialogContent>

      {errorMessage && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography variant="body2" color={errorRed}>
            {errorMessage}
          </Typography>
        </Box>
      )}

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={() => onClose()} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleAddEventClick}
          variant="contained"
          disabled={!!errorMessage}
          sx={{
            bgcolor: moovsBlue,
            "&:hover": {
              bgcolor: moovsBlue,
              opacity: 0.9,
            },
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddEventDialog;
